/**
 * Capacitor-helpers — alleen actief als de app als Android-APK draait.
 * In Tauri- of browser-mode zijn deze functies no-ops. Dynamische
 * imports zodat een PWA-build deze plugins nooit hoeft te bundelen
 * (Vite kapt de chunks netjes af).
 */

import { isTauriSync } from "./tauri";

let cachedNative: boolean | null = null;

/** True als we in een Capacitor-WebView draaien (= Android-APK).
 *  Resultaat wordt gecachet — Capacitor.isNativePlatform() leest een
 *  injected window-prop, mag elke render aangeroepen worden. */
export function isCapacitor(): boolean {
  if (cachedNative !== null) return cachedNative;
  if (typeof window === "undefined") return false;
  // Capacitor injecteert window.Capacitor vóór onze JS draait. De expliciete
  // check op .isNativePlatform() onderscheidt native-shell van een PWA
  // die per ongeluk de Capacitor-runtime zou laden.
  type Cap = { isNativePlatform?: () => boolean; getPlatform?: () => string };
  const w = window as unknown as { Capacitor?: Cap };
  if (!w.Capacitor) {
    cachedNative = false;
    return false;
  }
  cachedNative = w.Capacitor.isNativePlatform?.() === true;
  return cachedNative;
}

/** True als we in een Tauri-MSI of Capacitor-APK draaien — beide
 *  gebruiken Bearer-token-auth en eigen native push i.p.v. cookies +
 *  Web Push. Voor settings.ts en socket.ts is dit de juiste keuze. */
export function isNativeShell(): boolean {
  return isTauriSync() || isCapacitor();
}

/** Inhoud van een binnengekomen "Deel met Swift"-actie, klaar voor de
 *  deel-kiezer: optionele tekst (bv. een gedeelde link of bericht) plus
 *  de bestanden als gewone `File`-objecten die direct door uploadFile()
 *  verstuurd kunnen worden. */
export type SharedContent = { text: string | null; files: File[] };

/** Init voor de Android-app: status-bar styling + back-button-handling
 *  + app-lifecycle-hooks + pending-notification-conversation polling +
 *  inkomende deel-acties. Caller moet `onBack` opgeven die de huidige
 *  UI-state opruimt (lightbox, modals, drawer); pas als die niets meer
 *  hebben te sluiten gaat de hardware-back de app exitten.
 *  `onOpenConversation` wordt aangeroepen bij een notification-tap;
 *  `onShare` wanneer iemand vanuit een andere app naar Swift deelt
 *  (beide zowel bij cold-start als bij resume). */
export async function initCapacitorShell(opts: {
  onAppResume: () => void;
  onAppPause: () => void;
  onBack: () => boolean; // true = consumed, false = laat OS exitten
  onOpenConversation: (id: number) => void;
  onShare: (content: SharedContent) => void;
}): Promise<() => void> {
  if (!isCapacitor()) return () => undefined;

  const cleanups: Array<() => void> = [];

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    // Android: laat de WebView niet onder de systeem-statusbar tekenen.
    // Zonder dit valt de mobiele hamburger/header precies achter klok en
    // icons, vooral op Android 15+ waar edge-to-edge agressiever is.
    await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => undefined);
    await StatusBar.setStyle({ style: Style.Dark }).catch(() => undefined);
    await StatusBar.setBackgroundColor({ color: "#0f172a" }).catch(() => undefined);
  } catch {
    /* plugin niet geïnstalleerd of geen Android */
  }

  // Pending-conversation lezen + wissen. Onze native FCM-service schrijft
  // 'm naar SharedPreferences "CapacitorStorage" zodra een notification
  // getapt wordt; @capacitor/preferences leest dezelfde store.
  const consumePending = async (): Promise<void> => {
    try {
      const { Preferences } = await import("@capacitor/preferences");
      const r = await Preferences.get({ key: "swift_pending_open_conversation_id" });
      if (!r.value) return;
      const id = Number.parseInt(r.value, 10);
      await Preferences.remove({ key: "swift_pending_open_conversation_id" });
      if (Number.isFinite(id) && id > 0) opts.onOpenConversation(id);
    } catch {
      /* */
    }
  };

  void consumePending(); // cold-start

  // Pending-share lezen + wissen. MainActivity.handleShareIntent schrijft
  // 'm naar dezelfde store; bestanden staan in de app-cache en lezen we
  // via @capacitor/filesystem terug tot File-objecten.
  const consumePendingShare = async (): Promise<void> => {
    try {
      const { Preferences } = await import("@capacitor/preferences");
      const r = await Preferences.get({ key: "swift_pending_share" });
      if (!r.value) return;
      await Preferences.remove({ key: "swift_pending_share" });

      const parsed = JSON.parse(r.value) as {
        text: string | null;
        files: Array<{ name: string; mime: string; path: string }>;
      };

      const files: File[] = [];
      if (parsed.files && parsed.files.length > 0) {
        const { Filesystem, Directory } = await import("@capacitor/filesystem");
        for (const f of parsed.files) {
          try {
            const res = await Filesystem.readFile({ path: f.path, directory: Directory.Cache });
            const b64 = typeof res.data === "string" ? res.data : "";
            const blob = base64ToBlob(b64, f.mime);
            files.push(new File([blob], f.name, { type: f.mime }));
            // Opruimen zodra ingelezen — de WebView heeft 'm nu in geheugen.
            await Filesystem.deleteFile({ path: f.path, directory: Directory.Cache }).catch(
              () => undefined,
            );
          } catch {
            /* onleesbaar bestand overslaan, rest blijft bruikbaar */
          }
        }
      }

      const text = parsed.text ?? null;
      if (!text && files.length === 0) return;
      opts.onShare({ text, files });
    } catch {
      /* */
    }
  };
  void consumePendingShare(); // cold-start

  try {
    const { App } = await import("@capacitor/app");
    const stateHandle = await App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        opts.onAppResume();
        void consumePending();
        void consumePendingShare();
      } else {
        opts.onAppPause();
      }
    });
    cleanups.push(() => void stateHandle.remove());
    const backHandle = await App.addListener("backButton", async () => {
      const consumed = opts.onBack();
      if (!consumed) {
        await App.exitApp().catch(() => undefined);
      }
    });
    cleanups.push(() => void backHandle.remove());
  } catch {
    /* */
  }

  try {
    const { Keyboard } = await import("@capacitor/keyboard");
    await Keyboard.setResizeMode({ mode: "native" as never }).catch(() => undefined);
  } catch {
    /* */
  }

  return () => cleanups.forEach((c) => c());
}

/** Device-label voor de sessies-lijst, identiek aan PWA-detectie maar
 *  met "Android (Swift)" als de native shell actief is. */
export function capacitorDeviceLabel(): string {
  return "Android (Swift)";
}

/** Zet een base64-string (zoals Filesystem.readFile teruggeeft) om in een
 *  Blob. Via byte-array zodat ook binaire bestanden (afbeeldingen, PDF's)
 *  intact blijven — niet via fetch(data-URL) want dat faalt op grote
 *  payloads in de WebView. */
function base64ToBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
